import type { LucideIcon } from 'lucide-react';
import { BookPlus, CalendarPlus, FilePenLine, FilePlus, Handshake, ListPlus, PackagePlus, Receipt, ShoppingBag, ShoppingCart, SquarePen, UserPlus, Users } from 'lucide-react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSessao } from '../../nucleo/sessao';
import { Cartao } from '../base';

/**
 * Fase 2 (W1, D31): "Ações rápidas" por permissão, no lugar dos atalhos fixos. Cada ação leva à aba do módulo com o
 * estado `acaoRapida`; o `SlotAcaoPrimaria` aciona o botão primário da aba assim que ele aparece (o formulário de novo
 * registro abre sem um clique a mais). Só aparece o que o usuário pode criar, com o módulo instalado e a aba permitida.
 */
export interface AcaoRapida {
  chave: string;
  modulo: string;
  /** Permissão de criar no módulo. */
  permissao: string;
  /** Permissão da aba de destino. */
  permissaoAba: string;
  para: string;
  icone: LucideIcon;
  /** Fase 2 (W8): só no modo de operação citado (o pedido de compra é do modo detalhado de Compras). */
  modo?: { chave: string; em: string[] };
}

export const ACOES_RAPIDAS: AcaoRapida[] = [
  { chave: 'fatura', modulo: 'contas', permissao: 'contas.criar', permissaoAba: 'contas.ver', para: '/financeiro/contas/a-receber', icone: FilePlus },
  { chave: 'contaPagar', modulo: 'contas', permissao: 'contas.criar', permissaoAba: 'contas.ver', para: '/financeiro/contas/a-pagar', icone: Receipt },
  { chave: 'lancamento', modulo: 'financeiro', permissao: 'financeiro.criar', permissaoAba: 'financeiro.ver', para: '/financeiro/financeiro/entradas-saidas', icone: SquarePen },
  { chave: 'cliente', modulo: 'cadastros', permissao: 'cadastros.criar', permissaoAba: 'cadastros.ver', para: '/cadastros/cadastros/pessoas', icone: UserPlus },
  { chave: 'oportunidade', modulo: 'pipeline', permissao: 'pipeline.criar', permissaoAba: 'pipeline.ver', para: '/comercial/pipeline/pipeline', icone: Handshake },
  // Fase 2 (W21, K7): contrato (Assinaturas) e tarefa (Tarefas), só com o módulo instalado e a permissão de criar.
  { chave: 'contrato', modulo: 'assinaturas', permissao: 'assinaturas.criar', permissaoAba: 'assinaturas.ver', para: '/comercial/assinaturas/contratos', icone: FilePenLine },
  { chave: 'agendamento', modulo: 'agenda', permissao: 'agenda.criar', permissaoAba: 'agenda.ver', para: '/agenda/agenda/semana', icone: CalendarPlus },
  { chave: 'tarefa', modulo: 'tarefas', permissao: 'tarefas.criar', permissaoAba: 'tarefas.ver', para: '/operacao/tarefas/kanban', icone: ListPlus },
  { chave: 'compra', modulo: 'compras', permissao: 'compras.criar', permissaoAba: 'compras.ver', para: '/operacao/compras/compras', icone: ShoppingBag },
  { chave: 'pedidoCompra', modulo: 'compras', permissao: 'compras.criar', permissaoAba: 'compras.ver', para: '/operacao/compras/pedidos', icone: ShoppingCart, modo: { chave: 'compras.modo', em: ['detalhado'] } },
  { chave: 'produto', modulo: 'estoque', permissao: 'estoque.criar', permissaoAba: 'estoque.ver', para: '/cadastros/estoque/produtos', icone: PackagePlus },
  { chave: 'funcionario', modulo: 'pessoas', permissao: 'pessoas.criar', permissaoAba: 'pessoas.ver', para: '/pessoas/pessoas/funcionarios', icone: Users },
  { chave: 'curso', modulo: 'treinamento', permissao: 'treinamento.criar', permissaoAba: 'treinamento.ver', para: '/treinamento/treinamento/cursos', icone: BookPlus },
];

/** Ações que o usuário pode usar agora (função pura para teste). */
export function acoesPermitidas(pode: (p: string) => boolean, instalado: (m: string) => boolean, acoes: AcaoRapida[] = ACOES_RAPIDAS, modo: (chave: string) => string | undefined = () => undefined) {
  return acoes.filter((a) => instalado(a.modulo) && pode(a.permissao) && pode(a.permissaoAba) && (!a.modo || a.modo.em.includes(modo(a.modo.chave) ?? '')));
}

export function AcoesRapidas() {
  const { t } = useTranslation();
  const s = useSessao();
  const acoes = acoesPermitidas(s.pode, s.instalado, ACOES_RAPIDAS, s.modo);
  if (!acoes.length) return null;
  return (
    <Cartao titulo={t('inicio.acoesRapidas')}>
      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {acoes.map((a, idx) => {
          const Icone = a.icone;
          return (
            // O primeiro é o alvo do passo "primeira ação" do tour (o slot do Início fica vazio).
            <li key={a.chave} data-tour={idx === 0 ? 'primeira-acao' : undefined}>
              <Link to={a.para} state={{ acaoRapida: true }} className="flex items-center gap-3 rounded-lg border border-borda px-3 py-3 text-sm hover:bg-sup-2">
                <Icone className="size-4 text-acento-forte" aria-hidden />
                <span className="flex-1 font-medium">{t(`inicio.acoes.${a.chave}`)}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </Cartao>
  );
}
